import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHero from "../PageHero.jsx";
import InventorySurface from "../InventorySurface.jsx";
import DataTable from "../DataTable.jsx";
import ScrollArea from "../ScrollArea.jsx";
import ColumnPicker from "../ColumnPicker.jsx";
import SegmentedTabs from "../SegmentedTabs.jsx";
import ColumnHeaderRow from "../ColumnHeaderRow.jsx";
import { useDataTableColumns } from "../../lib/columnSelection.js";
import { findContentScrollRoot } from "../../lib/scrollRoot.js";
import { usePagedInventoryRows, useQuery } from "../../lib/dataClient.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { inventorySurfacesEnabled } from "../../lib/featureFlags.js";
import {
  INVENTORY_COLUMNS,
  INVENTORY_DEFAULT_VISIBLE,
  renderInventoryCell,
} from "../endpoints/inventoryTable.jsx";
import { useDebouncedValue } from "../../lib/useDebouncedValue.js";
import { inventoryRangeLabel } from "../../lib/inventoryRows.js";
import EmptyState, { ConnectEndpointsAction, useTenantEmpty } from "../EmptyState.jsx";

// Inventory SCOPE lens — available at EVERY scope as a toggle beside the
// overview. Renders the endpoint-inventory table (the same column schema,
// cells, and column prefs as the admin Endpoints page — the core is shared
// via components/endpoints/inventoryTable.jsx) scoped down to the
// endpoints active in the current scope+range: the same endpoint set the
// nav sidebar lists, but with the inventory's full metadata snapshot
// (agent/watchdog versions, OS, hardware, installed AI agents) in a
// usable table form factor.
//
// Tabs: endpoint listing only for now. The plan is more inventory tabs
// (identities et al.) once the identity work lands — the strip below is
// the affordance they'll slot into.

// The scope's active-endpoint count (the "In scope" tile) comes from the
// sidebar's dimension-entries query. 1000 covers any realistic scope.
const SCOPE_IDS_LIMIT = 1000;
export default function InventoryBody({ nav, slot, ctx }) {
  const { expanded, onToggleExpand } = slot ?? {};
  const scopePath = nav?.scopePath || [];
  const scoped = scopePath.length > 0;
  // The seen tabs render only whole-fleet at the root scope with the inventory
  // flag on (see InventorySurface's scale guard). Fetch their per-endpoint
  // aggregates under that SAME condition: a scoped view hides the charts, so a
  // whole-fleet per-endpoint pull would be wasted — and there's no way to narrow
  // them server-side without a >1024-id IN-list that 400s. Gating the fetch on
  // the render condition also means the flag-off default fires no request (so no
  // pre-#2162 ENDPOINT_STATUS 400 for tenants who never see the tabs).
  const seenAllowed = inventorySurfacesEnabled() && !scoped;

  // Endpoints active in this scope+range — same population the sidebar's
  // ENDPOINTS dim lists.
  const { data: dimEntries } = useQuery("getDimensionEntries", {
    dimKey: "endpoints",
    parentScopePath: scopePath,
    limit: SCOPE_IDS_LIMIT,
    range: ctx?.range,
  }, { enabled: scoped });
  const rawScopeTotal = dimEntries?.total ?? (dimEntries?.entries?.length ?? 0);

  // Inventory Surface charts — both tabs aggregated SERVER-SIDE (ClickHouse
  // groups endpoint_metadata_current by installed tool / OS family), so the
  // fleet-composition headline is exact regardless of how many rows the table
  // below paged in. The scope flows straight through as `scopePath`: the query
  // layer roots at the whole fleet when it's empty, else runs over the
  // endpoints active in scope via a PROMPT→ENDPOINT edge-join — no client-side
  // endpoint-id list, so no SCOPE_IDS_LIMIT cap (unlike the sidebar's id set,
  // which still feeds the table below). `useQuery` already holds these until any
  // adapter scope step (e.g. identity→endpoints) hydrates. `rows` still flows to
  // InventorySurface as a graceful fallback when a server aggregation is
  // unavailable (deploy window before the dims ship, or a transient error).
  // At the root, one active-device summary supplies every total and chart.
  // Scoped views retain their graph-backed aggregations because their scope
  // joins are not part of the metadata API contract.
  const { data: activeDeviceSummary } = useQuery(
    "getActiveDeviceInventorySummary",
    {},
    { enabled: !scoped },
  );
  const useGraphFallback = scoped || activeDeviceSummary === null;
  const { data: scopedAgentsAgg, loading: agentsLoading } = useQuery(
    "getInventoryAgentDistribution",
    { scopePath, range: ctx?.range },
    { enabled: useGraphFallback },
  );
  const { data: scopedOsAgg, loading: osLoading } = useQuery(
    "getInventoryOsDistribution",
    { scopePath, range: ctx?.range },
    { enabled: useGraphFallback },
  );
  const agentsAgg = !scoped && activeDeviceSummary
    ? { groups: activeDeviceSummary.agents, endpointsTotal: activeDeviceSummary.total }
    : scopedAgentsAgg;
  const osAgg = !scoped && activeDeviceSummary
    ? { groups: activeDeviceSummary.operatingSystems }
    : scopedOsAgg;
  // At the tenant root, the composition query is the active-device inventory
  // projection shared with Endpoints > Overview. The explorer dimension count
  // remains activity-based, so using it here would reintroduce the two totals
  // this surface is meant to reconcile.
  const scopeTotal = !scoped && agentsAgg?.endpointsTotal != null
    ? agentsAgg.endpointsTotal
    : rawScopeTotal;
  // Server-side count of endpoints that are active in scope AND have a metadata
  // row — drives the "No snapshot" tile (In scope - this) without deriving it
  // from the paginating client rows, so the header tiles stop jumping as pages
  // load and stay correct at scale.
  const { data: scopedActiveWithMeta } = useQuery(
    "getInventoryActiveWithMetadata",
    { scopePath, range: ctx?.range },
    { enabled: scoped },
  );
  const activeWithMeta = !scoped && activeDeviceSummary
    ? { count: activeDeviceSummary.total }
    : scopedActiveWithMeta;
  // First-seen / Last-seen charts — whole-fleet per-endpoint earliest / latest
  // observed metadata change (first-seen = MIN(change_time); last-seen =
  // MAX(received_at) over ENDPOINT_STATUS; see executeFirstSeenByEndpoint /
  // executeLastSeenByEndpoint). No scope/range params — each spans the full
  // retention window and buckets whole-fleet. Gated on `seenAllowed` so they
  // only fetch when the tabs will actually render (flag on + root scope).
  const { data: firstSeenAgg, loading: firstSeenLoading, error: firstSeenError } = useQuery("getFirstSeenByEndpoint", {}, { enabled: seenAllowed });
  const { data: lastSeenAgg, loading: lastSeenLoading, error: lastSeenError } = useQuery("getLastSeenByEndpoint", {}, { enabled: seenAllowed });

  // Table search + sort drive a SERVER-SIDE query (getInventoryRows): the
  // analytics graph scopes, searches, sorts, and dedupes across the whole
  // scope, so there's no client-side filter/sort or per-page id cap. Search is
  // debounced so a keystroke burst fires one query.
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [sortBy, setSortBy] = useState({ id: "snapshotTime", dir: "desc" });
  const onSortChange = useCallback((id) => {
    setSortBy((cur) =>
      cur?.id === id
        ? { id, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { id, dir: "asc" },
    );
  }, []);

  // Per-endpoint rows for the scope, served by the graph — already flattened to
  // the shared table shape (the two JSON columns arrive as strings and are
  // parsed in the reshape). Page zero replaces on scope/search/sort changes;
  // later pages append in the same deterministic server order.
  const {
    rows: inventoryRows,
    total,
    loadedCount: fetchedCount,
    hasMore,
    loadMore,
    loading: invLoading,
    error: invError,
  } = usePagedInventoryRows({
    scopePath,
    range: ctx?.range,
    search: debouncedSearch,
    sortBy,
  });
  const rows = inventoryRows;
  // Active in scope but absent from the inventory — server-side capture or an
  // endpoint that never reported a metadata snapshot. The gap between "we see
  // its traffic" and "we manage it". Server-side (In scope - active-with-metadata).
  const missingCount = activeWithMeta?.count != null
    ? Math.max(0, scopeTotal - activeWithMeta.count)
    : 0;

  // Shares the admin page's storageKey on purpose — same table, same
  // user column prefs on both surfaces.
  const cols = useDataTableColumns({
    storageKey: "endpointInventory",
    columns: INVENTORY_COLUMNS,
    defaultVisible: INVENTORY_DEFAULT_VISIBLE,
  });

  const isFiltering = debouncedSearch.trim().length > 0;
  const loading = invLoading;
  // Only resolve the tenant-connected question once this lens is actually
  // rendering an empty table — a populated scope never spends the fetch.
  const tenantEmpty = useTenantEmpty({ enabled: !loading && rows.length === 0 });

  // The table virtualizes, so a bare "138" reads as if the rest never load.
  // Track the visible row band and show a live "showing X-Y of N" range instead,
  // so scrolling visibly advances through all N. Null (→ plain count) while
  // filtering, before the first measured report, or when the reader can see the
  // whole list at once.
  const [winRange, setWinRange] = useState(null);
  const rangeLabel = inventoryRangeLabel(winRange, rows.length, isFiltering);

  // Clicking a row drills Explore into that endpoint's scope — the lens
  // is a pivot surface, not a dead end.
  const onRowClick = useCallback((row) => {
    if (!row?.id) return;
    ctx?.onNavigatePath?.([...scopePath, { dim: "endpoints", ids: [row.id], id: row.id }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.onNavigatePath, JSON.stringify(scopePath)]);

  // Frozen-header sync. Inventory is a WIDE, all-pixel-column table (memory's
  // tables carry a flex column, so they fit the viewport and never scroll
  // sideways — which is why the simple page-scroll model "just works" there).
  // Here the body owns a HORIZONTAL scroll inside an `overflow-x-auto` wrapper so
  // the page itself never overflows (the chrome can't get dragged off-screen).
  // But that wrapper is a scroll context for both axes, so a `sticky top` header
  // INSIDE it would pin to the wrapper (which doesn't scroll vertically) and
  // scroll away with the rows. So the column header lives in the PINNED chrome
  // instead (sticks vertically against the page) and we mirror its horizontal
  // scroll to the body's, both ways, keeping the columns aligned. The header's
  // own scrollbar is hidden (data-hide-scrollbar) since it only tracks the body;
  // horizontal scroll is driven by the body (trackpad / its scrollbar).
  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  useEffect(() => {
    const header = headerScrollRef.current;
    const body = bodyScrollRef.current;
    if (!header || !body) return undefined;
    // Value-checked rather than locked: assigning an equal scrollLeft fires no
    // scroll event, and the echo from a real change finds the two already equal
    // and no-ops — so the two listeners converge instead of looping.
    const onHeader = () => {
      if (Math.round(body.scrollLeft) !== Math.round(header.scrollLeft)) body.scrollLeft = header.scrollLeft;
    };
    const onBody = () => {
      if (Math.round(header.scrollLeft) !== Math.round(body.scrollLeft)) header.scrollLeft = body.scrollLeft;
    };
    header.addEventListener("scroll", onHeader, { passive: true });
    body.addEventListener("scroll", onBody, { passive: true });
    return () => {
      header.removeEventListener("scroll", onHeader);
      body.removeEventListener("scroll", onBody);
    };
  }, []);

  return (
    // Page-scroll model, matching the memory store: the whole page scrolls
    // VERTICALLY (hero scrolls away; the search/filter toolbar AND the column
    // header stick at the top). The body scrolls HORIZONTALLY inside its own
    // wrapper (so the page never overflows sideways), and the header mirrors it
    // (see the frozen-header sync above). The grid track is pinned to the
    // container width (minmax(0,1fr)) so both the header strip and the body
    // resolve to the viewport width and scroll inside themselves, not past.
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid grid-cols-[minmax(0,1fr)] gap-[10px] pb-[18px]">
      {/* Hero + the "Endpoints" tab collapse away when the surface is
          expanded fullscreen; the InventorySurface between them carries the
          data-expand-target ContentShell lifts (same wiring as XRayBody and
          the overview's Activity Surface). */}
      <div data-expand-collapse>
        <PageHero
          eyebrow="Inventory"
          title="Endpoints in scope"
          detail={scoped
            ? "Inventory for endpoints with AI activity in this scope and the selected date range."
            : "Inventory for endpoints with an agent heartbeat in the last 30 days. Find quiet endpoints under Endpoints > Overview > Inactive."}
          // The three tiles are gated OFF by default behind dashboard:inventory
          // (O-2696 exposure gate; empty metrics = no tiles). The counts below
          // stay computed regardless — they still feed the truncation note and
          // table empty-state. All three are server-side, so when shown they
          // don't jump as the table's client rows paginate in: "Inventoried" =
          // agentsAgg.endpointsTotal (buildEndpointAggRoot — whole-fleet-unless-
          // scoped, no id-list), matching the composition tabs and scaling to
          // large clusters; "No snapshot" = In scope - active-with-metadata.
          metrics={inventorySurfacesEnabled() ? [
            {
              label: "In scope",
              value: scopeTotal ? formatCompact(scopeTotal) : "-",
              note: scoped ? "active in this scope + range" : "seen in the last 30 days",
            },
            { label: "Inventoried", value: agentsAgg ? formatCompact(agentsAgg.endpointsTotal) : "-", note: "with a metadata snapshot" },
            {
              label: "No snapshot",
              value: activeWithMeta?.count != null ? formatCompact(missingCount) : "-",
              note: missingCount ? "seen in traffic, never reported metadata" : "",
            },
          ] : []}
        />
      </div>
      {/* Server-side only — the surface takes NO client rows (that's what made
          the numbers jump / undercount at scale). It reads only the analytics
          aggregates below. The loaded rows stay for the table beneath it. */}
      <InventorySurface
        agentsAgg={agentsAgg}
        osAgg={osAgg}
        firstSeenAgg={firstSeenAgg}
        firstSeenError={firstSeenError}
        lastSeenAgg={lastSeenAgg}
        lastSeenError={lastSeenError}
        // The seen tabs are whole-fleet only (hidden once a scope is committed),
        // so the surface takes just this flag — never the capped id set.
        scoped={scoped}
        agentsLoading={agentsLoading}
        osLoading={osLoading}
        firstSeenLoading={firstSeenLoading}
        lastSeenLoading={lastSeenLoading}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
      />
      <div data-expand-collapse>
        <SegmentedTabs
          tabs={[{ id: "endpoints", eyebrow: "Inventory", title: "Endpoints" }]}
          active="endpoints"
        />
      </div>
      {/* Search / filter header — sticky-top. Viewport-width (the page never
          overflows horizontally now), so a plain sticky-top pins it cleanly. */}
      <div data-expand-collapse className="sticky top-0 z-20 grid gap-[8px] bg-[color:var(--bg)] pt-[6px] shadow-[0_1px_0_var(--line-soft)]">
        <div className="grid items-end gap-[8px] [grid-template-columns:minmax(0,1fr)_auto]">
          <label className="grid gap-[5px]">
            <span className={NOTE_CLASS}>Search</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Hostname, agent version, registered user, model, OS, …"
              className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
            />
          </label>
          <ColumnPicker {...cols.pickerProps} variant="input" />
        </div>
        <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] py-[2px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
          <span>
            {rangeLabel ? (
              <>
                showing{" "}
                <strong className="text-[color:var(--ink)]">
                  {formatCompact(rangeLabel.start)}-{formatCompact(rangeLabel.end)}
                </strong>{" "}
                of <strong className="text-[color:var(--ink)]">{formatCompact(rangeLabel.total)}</strong>
              </>
            ) : (
              <>
                <strong className="text-[color:var(--ink)]">{formatCompact(rows.length)}</strong>{" "}
                {isFiltering ? "matching" : "shown"}
              </>
            )}
          </span>
          {hasMore ? (
            <span className={NOTE_CLASS}>
              of <strong className="text-[color:var(--ink)]">{formatCompact(total)}</strong> {isFiltering ? "matching" : "in scope"}
            </span>
          ) : null}
          {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
          {invError ? (
            <span className="text-red-400">{invError.message || String(invError)}</span>
          ) : null}
        </div>
        {/* Column header — pinned with the chrome (sticks vertically), mirrors
            the body's horizontal scroll. Scrollbar hidden (it tracks the body's
            scroll, so its own bar would just be visual noise up top); the strip
            stays scrollable programmatically and by trackpad so the sync holds. */}
        <div ref={headerScrollRef} data-hide-scrollbar className="min-w-0 overflow-x-auto">
          <ColumnHeaderRow cols={cols} sortBy={sortBy} onSortChange={onSortChange} resizable />
        </div>
      </div>

      {/* Body owns the horizontal scroll; the header above mirrors it.
          aria-busy marks the table region while the server-side query is in
          flight (the loading-state contract in dashboard/AGENTS.md). */}
      <div ref={bodyScrollRef} data-lock-width data-expand-collapse aria-busy={loading || undefined} className="min-w-0 overflow-x-auto">
        <DataTable
          cols={cols}
          rows={rows}
          getRowId={(r) => r.id || r.hostname}
          renderCell={renderInventoryCell}
          onRowClick={onRowClick}
          sortBy={sortBy}
          onSortChange={onSortChange}
          loading={loading}
          skeletonRows={6}
          showHeader={false}
          findScrollContainer={findContentScrollRoot}
          onVisibleRange={setWinRange}
          empty={
            isFiltering ? (
              <EmptyState
                className="my-[12px]"
                title="No endpoints in scope match this search"
                note="Clear the search to bring the scope's endpoints back."
              />
            ) : tenantEmpty ? (
              <EmptyState
                className="my-[12px]"
                title="Origin isn't connected to any endpoints yet"
                note="Install the agent on one machine and it appears here within about a minute."
                action={<ConnectEndpointsAction />}
              />
            ) : (
              <EmptyState
                className="my-[12px]"
                title="No endpoints with a metadata snapshot in this scope"
                note="Endpoints appear here once their agents report a metadata snapshot."
              />
            )
          }
          footer={
            hasMore ? (
              <div className="grid place-items-center pt-[8px] pb-[10px]">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loading}
                  className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 cursor-pointer"
                >
                  Load more · {formatCompact(total - fetchedCount)} remaining
                </button>
              </div>
            ) : null
          }
        />
      </div>
    </ScrollArea>
  );
}
